"use client";

import { useI18n } from "@/i18n";
import { ActionButton, Dots, InputOTP, InputOTPGroup, InputOTPSlot } from "@repo/uikit";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { ArrowLeft } from "lucide-react";

interface CodeStepProps {
  codeValue: string;
  isVerifying: boolean;
  hasError?: boolean;
  codeError?: string;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  onGoBack: () => void;
  isSending: boolean;
  canResend: boolean;
  resendSeconds: number;
  onResend: () => void;
}

export default function CodeStep({
  codeValue,
  isVerifying,
  hasError = false,
  codeError = "",
  onCodeChange,
  onVerify,
  onGoBack,
  isSending,
  canResend,
  resendSeconds,
  onResend,
}: CodeStepProps) {
  const { t } = useI18n();
  const canVerify = codeValue.length === 6 && !isVerifying && !isSending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canVerify) onVerify();
      }}
      className="space-y-4"
    >
      <div className="mt-6">
        <p className="text-sm font-semibold text-foreground" data-i18n="auth.confirmationCode">
          {t("auth.confirmationCode")}
        </p>
      </div>

      <div className="flex justify-center w-full">
        <InputOTP
          data-testid="login-otp-input"
          value={codeValue}
          disabled={isVerifying || isSending}
          maxLength={6}
          pattern={REGEXP_ONLY_DIGITS}
          onChange={onCodeChange}
          autoFocus
        >
          <InputOTPGroup className="flex justify-between gap-2">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <InputOTPSlot
                key={index}
                data-testid={`login-otp-slot-${index}`}
                className={`h-10 w-10 rounded-md text-base font-semibold border first:rounded-md last:rounded-md transition-colors ${
                  hasError ? "border-destructive/70 bg-destructive/5 text-destructive" : "border-input bg-card text-foreground"
                }`}
                index={index}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {codeError ? (
        <p className="text-xs font-medium text-destructive" data-testid="login-code-error">
          {codeError}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-2 pt-2">
        <ActionButton
          type="button"
          variant="outline"
          data-testid="login-back-btn"
          data-i18n="auth.changeEmail"
          disabled={isVerifying || isSending}
          className="w-full text-xs text-muted-foreground"
          onClick={onGoBack}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t("auth.changeEmail")}</span>
        </ActionButton>
        <ActionButton
          type="submit"
          data-testid="login-verify-btn"
          data-i18n={isVerifying ? "auth.verifyingCode" : "auth.verifyCode"}
          loading={isVerifying}
          disabled={!canVerify}
          className="w-full"
        >
          {isVerifying ? (
            <Dots data-i18n="auth.verifyingCode">{t("auth.verifyingCode")}</Dots>
          ) : (
            <span data-i18n="auth.verifyCode">{t("auth.verifyCode")}</span>
          )}
        </ActionButton>
      </div>
      <ActionButton
        type="button"
        variant="outline"
        className="w-full"
        disabled={!canResend}
        onClick={onResend}
        data-testid="login-resend-btn"
      >
        {isSending
          ? t("auth.sending")
          : resendSeconds > 0
            ? t("auth.resendCodeCountdown", { seconds: resendSeconds })
            : t("auth.resendCode")}
      </ActionButton>
    </form>
  );
}
